// The signed-in person's workspaces and the one that is on — shared by the
// switcher, the top bar, the phone drawer and Home, so they never disagree.
//
// One module-level store (fetched once per page load) instead of a context
// provider: the shell mounts nothing new. A client with WORKSPACES_ENABLED
// off never calls the API at all.

import { useEffect, useMemo, useSyncExternalStore } from 'react';
import api from '@app/services/api';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { visibleWorkspaces, pickWorkspace } from '@platform/app-shell/frontend/utils/workspaces';
import { BRAND_NAME } from '@client/config/client.config';
import { WORKSPACES_ENABLED } from '@platform/app-shell/frontend/utils/workspacesEnabled';

const REMEMBER_KEY = 'stride.workspace';

const readRemembered = () => { try { return localStorage.getItem(REMEMBER_KEY); } catch { return null; } };
const readRequested = () => { try { return new URL(window.location.href).searchParams.get('ws'); } catch { return null; } };

let state = { definition: null, loaded: !WORKSPACES_ENABLED, chosen: readRequested() || readRemembered() };
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const snapshot = () => state;

let loading = null;
function load() {
  if (loading) return loading;
  // Switched off for this client: never ask, nothing changes.
  if (!WORKSPACES_ENABLED) { loading = Promise.resolve(); return loading; }
  loading = api.getWorkspaces()
    .then((d) => { state = { ...state, definition: d || null, loaded: true }; })
    .catch(() => { state = { ...state, definition: null, loaded: true }; }) // no endpoint = feature off
    .finally(emit);
  return loading;
}

export function chooseWorkspace(id) {
  try { localStorage.setItem(REMEMBER_KEY, id); } catch { /* per-viewer convenience only */ }
  state = { ...state, chosen: id };
  emit();
}

export default function useWorkspaces() {
  const { userGroups } = useAuth();
  const s = useSyncExternalStore(subscribe, snapshot);
  useEffect(() => { load(); }, []);
  return useMemo(() => {
    const visible = WORKSPACES_ENABLED ? visibleWorkspaces(s.definition, userGroups || [], BRAND_NAME) : [];
    const active = pickWorkspace({ visible, requested: s.chosen });
    return { loaded: s.loaded, visible, active, enabled: visible.length > 0 };
  }, [s, userGroups]);
}
